import React from "react";

import {
  TLBaseShape,
  RecordProps,
  T,
  HTMLContainer,
  BaseBoxShapeUtil,
  Rectangle2d,
  TLResizeInfo,
  Vec,
  useValue,
} from "@tldraw/tldraw";
import { getDashArray } from "../new-geo-shape-helpers";
import { defaultScale } from "../constants";
import { HOVER_EFFECT } from "./constants";
import { mixColors } from "./CustomCloudUtil";
import { ShapeUtilHelper } from "./ShapeUtilHelper";
import { ShapeShadowDefs, buildFilterRegion } from "./shadowUtils";
import { areCornersUniform, cornerRadiiCss, getCornerRadii, roundedRectPath } from "./utils/cornerRadii";

type CustomRectUtilType = TLBaseShape<
  "customRect",
  {
    w: number;
    h: number;
    fill: string;
    outlineColor: string;
    dash: "solid" | "dashed" | "dotted" | "dashed solid" | "custom";
    transparency: number;
    strokeDasharray?: string;
    strokeWidth?: number;
    strokeAlign?: "inside" | "center" | "outside";
    scale?: number;
    filterBlur?: number;
    isCreatedFilterBlur?: boolean;
    filterBlurValueWhenHidden?: number;
    rounded: number;
    // Optional per-corner overrides for `rounded`. When `undefined` the
    // corner falls back to the uniform `rounded` value. Edited via the
    // CornerRadiusPopover in the Alignment panel.
    roundedTL?: number;
    roundedTR?: number;
    roundedBR?: number;
    roundedBL?: number;
    borderWidth?: number;
    borderColor?: string;
    borderSolid?: "solid" | "dashed";
    borderTopStyle?: string;
    borderBottomStyle?: string;
    borderLeftStyle?: string;
    borderRightStyle?: string;
    isShowBorderTop?: boolean;
    isShowBorderBottom?: boolean;
    isShowBorderLeft?: boolean;
    isShowBorderRight?: boolean;
    outShadowArray?: {
      [key: string]: {
        id: string;
        dx: number;
        dy: number;
        shadowBlur: number;
        color: string;
        opacity: number;
        isHidden: boolean;
      };
    };
    inShadowArray?: {
      [key: string]: {
        id: string;
        dx: number;
        dy: number;
        shadowBlur: number;
        color: string;
        opacity: number;
        isHidden: boolean;
      };
    };
    previousBorderColor?: string;
    isCreatedBorderItem?: boolean;
    colorMix: {
      [key: string]: {
        color: string;
        isHidden: boolean;
      };
    };
    outlineColorMix: {
      [key: string]: {
        color: string;
        isHidden: boolean;
      };
    };
    isHiddenLayerBlur?: boolean;
    isHiddenBorder?: boolean;
    isShowIndicator?: boolean;
    isScale?: boolean;
    [key: string]: any;
  }
>;

export class CustomRectUtil extends BaseBoxShapeUtil<CustomRectUtilType> {
  private shapeUtilHelper: ShapeUtilHelper;

  constructor(editor: any) {
    super(editor);
    this.shapeUtilHelper = new ShapeUtilHelper(editor);
  }

  static override type = "customRect" as const;
  static override props: any;

  // Disable resize for mask shapes
  canResize(shape: CustomRectUtilType) {
    if (shape.meta?.isMaskShape) {
      return false;
    }
    return true; // Default: allow resize for normal rectangles
  }

  getDefaultProps(): CustomRectUtilType["props"] {
    return {
      w: 100,
      h: 100,
      fill: "#e3e3e3",
      outlineColor: "#000000",
      dash: "solid",
      rounded: 0,
      transparency: 1,
      strokeWidth: 0,
      strokeAlign: "inside",
      scale: defaultScale,
      filterBlur: 0,
      isCreatedFilterBlur: false,
      filterBlurValueWhenHidden: 0,
      borderWidth: 0,
      borderColor: "#000000",
      borderSolid: "solid",
      borderTopStyle: "solid",
      borderBottomStyle: "solid",
      borderLeftStyle: "solid",
      borderRightStyle: "solid",
      isShowBorderTop: true,
      isShowBorderBottom: true,
      isShowBorderLeft: true,
      isShowBorderRight: true,
      outShadowArray: {},
      inShadowArray: {},
      previousBorderColor: "#000000",
      isCreatedBorderItem: false,
      colorMix: {
        "0": { color: "#e3e3e3", isHidden: false },
      },
      outlineColorMix: {},
      isHiddenLayerBlur: false,
      isHiddenBorder: false,
      isShowIndicator: true,
      isScale: false,
    };
  }

  getGeometry(shape: CustomRectUtilType) {
    return this.shapeUtilHelper.calculateAdjustedDimensions(shape);
  }

  hideSelectionBoundsFg(_shape: CustomRectUtilType): boolean {
    return false; // outline visibility is controlled by isChangingStyle in useHideSelectionOnChange
  }
  hideSelectionBoundsBg(shape: CustomRectUtilType): boolean {
    return false; // Always show selection bounds regardless of opacity
  }
  hideResizeHandles(shape: CustomRectUtilType): boolean {
    return false; // Always show resize handles regardless of opacity
  }

  isAspectRatioLocked(shape: CustomRectUtilType): boolean {
    return !!shape?.props?.isScale;
  }

  component(shape: CustomRectUtilType) {
    const props = this.shapeUtilHelper.getSafeProps(shape);
    const {
      w,
      h,
      outlineColor,
      dash,
      rounded,
      transparency,
      strokeDasharray,
      strokeWidth,
      filterBlur,
      borderWidth,
      borderSolid,
      isShowBorderTop,
      isShowBorderBottom,
      isShowBorderLeft,
      isShowBorderRight,
      outShadowArray,
      inShadowArray,
      colorMix,
      outlineColorMix,
      isHiddenLayerBlur,
      isHiddenBorder,
      flipX,
      flipY,
    } = props;

    const strokeAlign = shape.props.strokeAlign || "inside";

    // Per-corner radii. When all four corners are equal we keep the
    // cheap `<rect rx ry>` rendering path to avoid churning the DOM for
    // every shape on the canvas; only when a user has actually edited
    // an individual corner do we switch to `<path d="...">`.
    const cornerRadii = getCornerRadii(shape.props);
    const cornersUniform = areCornersUniform(cornerRadii);
    const radiusCss = cornersUniform ? `${cornerRadii.tl}px` : cornerRadiiCss(cornerRadii);

    // Memoize the heavier derivations on the shape's prop references. tldraw
    // uses immutable updates: when a shape moves (only `x` / `y` change),
    // `colorMix` / `outlineColorMix` / `outShadowArray` / `inShadowArray`
    // keep their identity, so `useMemo` returns the cached value and we skip
    // the `Object.values(...).filter(...).map(...).mixColors(...)` work for
    // each of the N moving shapes per pointer move.
    //
    // Note: when these props are undefined (shape has no custom colors yet),
    // `getSafeProps` synthesizes a default object on every call → the memo
    // invalidates every render. The default is small (single color, empty
    // shadow arrays), so even the "miss" path is cheap.
    const calculatedFill = React.useMemo(() => {
      const visible = Object.values(colorMix).filter((c: any) => !c.isHidden);
      return visible.length > 0 ? mixColors(visible.map((c: any) => c.color)) : "transparent";
    }, [colorMix]);

    // Debug log for mask shapes
    if (shape.meta?.isMaskShape) {
      const clipPathStr = typeof shape.meta?.maskClipPath === "string" ? shape.meta.maskClipPath : "";
    }

    const calculatedOutlineColor = React.useMemo(() => {
      const visible = Object.values(outlineColorMix).filter((c: any) => !c.isHidden);
      return visible.length > 0 ? mixColors(visible.map((c: any) => c.color)) : "transparent";
    }, [outlineColorMix]);

    const shadowFilterId = React.useMemo(() => {
      const visibleOut = outShadowArray ? Object.values(outShadowArray).filter((shadow: any) => !shadow.isHidden) : [];
      const visibleIn = inShadowArray ? Object.values(inShadowArray).filter((shadow: any) => !shadow.isHidden) : [];
      return [
        ...visibleOut.map((shadow: any) => `url(#${shadow.id})`),
        ...visibleIn.map((shadow: any) => `url(#${shadow.id})`),
      ].join(" ");
    }, [outShadowArray, inShadowArray]);
    const innerShadowFilterId = `inner-shadow-${shape.id}`; // Unique ID for the inner shadow filter
    const dashArray = strokeDasharray || getDashArray(dash);

    // Subscribe only to the *boolean* "is this shape hovered" instead of the
    // full `getHoveredShape()` record. Without this, every shape's component()
    // re-runs on every hover transition (signal fans out to all subscribers),
    // producing O(N) re-renders per pointer move. `useValue` short-circuits on
    // equality, so only the entering + leaving shape actually re-render.
    const editor = this.editor;
    const isHovered = useValue("isHovered", () => editor.getHoveredShapeId() === shape.id, [editor, shape.id]);

    // Check if shape has clip-path from mask group
    const maskClipPath = shape.meta?.maskClipPath;

    const isLineShape = [
      "shape:frontBorderDashLine",
      "shape:backBorderDashLine",
      "shape:frontRedLine",
      "shape:backRedLine",
      "shape:frontBlackLine",
      "shape:backBlackLine",
    ].includes(shape.id);

    // Calculate stroke positioning based on strokeAlign
    const getStrokePositioning = () => {
      const sw = strokeWidth || 0;
      switch (strokeAlign) {
        case "outside":
          return {
            rectX: 0,
            rectY: 0,
            rectWidth: w,
            rectHeight: h,
            strokeX: -sw / 2,
            strokeY: -sw / 2,
            strokeWidth: w + sw,
            strokeHeight: h + sw,
          };
        case "center":
          return {
            rectX: sw / 2,
            rectY: sw / 2,
            rectWidth: w - sw,
            rectHeight: h - sw,
            strokeX: 0,
            strokeY: 0,
            strokeWidth: w,
            strokeHeight: h,
          };
        case "inside":
        default:
          return {
            rectX: sw / 2,
            rectY: sw / 2,
            rectWidth: w - sw,
            rectHeight: h - sw,
            strokeX: sw / 2,
            strokeY: sw / 2,
            strokeWidth: w - sw,
            strokeHeight: h - sw,
          };
      }
    };

    const strokePos = getStrokePositioning();

    // Calculate shadow dimensions including stroke for proper shadow rendering
    const shadowW = w + (strokeWidth || 0);
    const shadowH = h + (strokeWidth || 0);

    // Calculate flip transform style
    const getFlipStyle = () => {
      if (!flipX && !flipY) return {};
      const scale = `scale(${flipX ? -1 : 1}, ${flipY ? -1 : 1})`;
      const translate = `translate(${flipX ? w : 0}px, ${flipY ? h : 0}px)`;
      return {
        transform: `${translate} ${scale}`,
        transformOrigin: "0 0",
      };
    };

    return (
      <HTMLContainer id={shape.id}>
        <div
          style={{
            width: w,
            height: h,
            clipPath: maskClipPath ? `path('${maskClipPath}')` : undefined,
            ...getFlipStyle(),
          }}
        >
          <svg
            // SVG's default viewport behaviour is to CLIP content to the
            // bounds of the <svg> element (which is sized to the shape's
            // w×h). Shadows / blurs extend past those bounds, so without
            // `overflow: visible` the shadow halo gets sharply cut off
            // at the right / bottom edge — visible at low blur and
            // imperceptible above ~blur 12 because the halo's alpha at
            // the cutoff is near zero. We set BOTH the CSS property and
            // the SVG attribute: some browsers/contexts don't honor CSS
            // `overflow` on the root <svg> reliably.
            overflow="visible"
            style={{
              overflow: "visible",
              borderRadius: radiusCss,
              borderTop:
                isShowBorderTop && !isHiddenBorder
                  ? `${borderWidth}px ${borderSolid ?? "solid"} ${calculatedOutlineColor}`
                  : "none",
              borderBottom:
                isShowBorderBottom && !isHiddenBorder
                  ? `${borderWidth}px ${borderSolid ?? "solid"} ${calculatedOutlineColor}`
                  : "none",
              borderLeft:
                isShowBorderLeft && !isHiddenBorder
                  ? `${borderWidth}px ${borderSolid ?? "solid"} ${calculatedOutlineColor}`
                  : "none",
              borderRight:
                isShowBorderRight && !isHiddenBorder
                  ? `${borderWidth}px ${borderSolid ?? "solid"} ${calculatedOutlineColor}`
                  : "none",
            }}
            viewBox={`0 0 ${w} ${h}`}
          >
            <defs>
              {/* Gradient definition */}
              {Object.values(colorMix).filter((c: any) => !c.isHidden).length > 0 && (
                <linearGradient id={`gradient-${shape.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                  {Object.values(colorMix)
                    .filter((c: any) => !c.isHidden)
                    .map((colorObj: any, index) => (
                      <stop
                        key={index}
                        offset={`${
                          (index / (Object.values(colorMix).filter((c: any) => !c.isHidden).length - 1)) * 100
                        }%`}
                        stopColor={colorObj.color}
                      />
                    ))}
                </linearGradient>
              )}
              <ShapeShadowDefs
                outShadowArray={outShadowArray}
                inShadowArray={inShadowArray}
                shapeWidth={w}
                shapeHeight={h}
              />

              {/* Blur filter definition. Region is sized to the actual
                  blur so big values don't get clipped to a square at
                  the legacy 200% region. */}
              {!isHiddenLayerBlur && filterBlur && filterBlur > 0 && (
                <filter id={`blur-${shape.id}`} {...buildFilterRegion({ blur: filterBlur }, w, h)}>
                  <feGaussianBlur stdDeviation={filterBlur} />
                </filter>
              )}
            </defs>
            {/* Invisible hitarea to capture pointer events regardless of opacity */}
            {cornersUniform ? (
              <rect
                className="shape-hitarea"
                x={0}
                y={0}
                width={w}
                height={h}
                fill="transparent"
                stroke="none"
                rx={rounded}
                ry={rounded}
              />
            ) : (
              <path
                className="shape-hitarea"
                d={roundedRectPath(0, 0, w, h, cornerRadii)}
                fill="transparent"
                stroke="none"
              />
            )}
            {/* Group for shadow that includes both shape and stroke */}
            <g
              filter={`${shadowFilterId}${
                !isHiddenLayerBlur && filterBlur && filterBlur > 0 ? ` url(#blur-${shape.id})` : ""
              }`}
              style={{ pointerEvents: "none" }}
            >
              {/* Main shape */}
              {cornersUniform ? (
                <rect
                  x={strokePos.rectX}
                  y={strokePos.rectY}
                  width={strokePos.rectWidth}
                  height={strokePos.rectHeight}
                  fill={isLineShape ? "transparent" : calculatedFill}
                  stroke="none"
                  rx={rounded}
                  ry={rounded}
                  fillOpacity={transparency}
                />
              ) : (
                <path
                  d={roundedRectPath(
                    strokePos.rectX,
                    strokePos.rectY,
                    strokePos.rectWidth,
                    strokePos.rectHeight,
                    cornerRadii
                  )}
                  fill={isLineShape ? "transparent" : calculatedFill}
                  stroke="none"
                  fillOpacity={transparency}
                />
              )}
              {/* Stroke overlay - rendered separately to not affect inner element */}
              {strokeWidth &&
                strokeWidth > 0 &&
                (cornersUniform ? (
                  <rect
                    x={strokePos.strokeX}
                    y={strokePos.strokeY}
                    width={strokePos.strokeWidth}
                    height={strokePos.strokeHeight}
                    fill="none"
                    stroke={
                      Object.entries(shape.props.outlineColorMix).length > 0 ? calculatedOutlineColor : outlineColor
                    }
                    strokeWidth={strokeWidth}
                    rx={rounded}
                    ry={rounded}
                    strokeOpacity={transparency}
                    strokeDasharray={dashArray}
                    strokeLinejoin="miter"
                  />
                ) : (
                  <path
                    d={roundedRectPath(
                      strokePos.strokeX,
                      strokePos.strokeY,
                      strokePos.strokeWidth,
                      strokePos.strokeHeight,
                      cornerRadii
                    )}
                    fill="none"
                    stroke={
                      Object.entries(shape.props.outlineColorMix).length > 0 ? calculatedOutlineColor : outlineColor
                    }
                    strokeWidth={strokeWidth}
                    strokeOpacity={transparency}
                    strokeDasharray={dashArray}
                    strokeLinejoin="miter"
                  />
                ))}
            </g>
          </svg>
          {isHovered && (
            <div
              style={{
                position: "absolute",
                top: -borderWidth!,
                left: -borderWidth!,
                width: w + borderWidth! * 2,
                height: h + borderWidth! * 2,
                pointerEvents: "none",
                zIndex: 999,
                opacity: 1,
                isolation: "isolate",
              }}
            >
              <svg
                width={w + borderWidth! * 2}
                height={h + borderWidth! * 2}
                viewBox={`0 0 ${w + borderWidth! * 2} ${h + borderWidth! * 2}`}
                style={{
                  fill: "none",
                  stroke: HOVER_EFFECT.COLOR,
                  strokeWidth: "2",
                  pointerEvents: "none",
                  opacity: 1,
                }}
              >
                {cornersUniform ? (
                  <rect
                    x={borderWidth!}
                    y={borderWidth!}
                    width={w}
                    height={h}
                    rx={rounded}
                    ry={rounded}
                    fill="none"
                    stroke={HOVER_EFFECT.COLOR}
                    strokeWidth="2"
                  />
                ) : (
                  <path
                    d={roundedRectPath(borderWidth!, borderWidth!, w, h, cornerRadii)}
                    fill="none"
                    stroke={HOVER_EFFECT.COLOR}
                    strokeWidth="2"
                  />
                )}
              </svg>
            </div>
          )}
        </div>
      </HTMLContainer>
    );
  }

  addDefaultColorMix(shape: CustomRectUtilType) {
    this.shapeUtilHelper.addDefaultColorMix(shape);
  }
  addDefaultOutShadow(shape: CustomRectUtilType) {
    this.shapeUtilHelper.addDefaultOutShadow(shape);
  }
  addDefaultInShadow(shape: CustomRectUtilType) {
    this.shapeUtilHelper.addDefaultInShadow(shape);
  }

  indicator(shape: CustomRectUtilType) {
    const {
      h,
      w,
      borderWidth,
      isShowBorderBottom,
      isShowBorderTop,
      isShowBorderLeft,
      isShowBorderRight,
      isShowIndicator,
      isHiddenBorder,
      strokeWidth = 0,
      rounded,
    } = shape.props;

    // Indicator should only outline the base shape, not include stroke width
    const adjustedHeight =
      h +
      (isShowBorderTop && !isHiddenBorder ? borderWidth ?? 0 : 0) +
      (isShowBorderBottom && !isHiddenBorder ? borderWidth ?? 0 : 0);

    const adjustedWidth =
      w +
      (isShowBorderLeft && !isHiddenBorder ? borderWidth ?? 0 : 0) +
      (isShowBorderRight && !isHiddenBorder ? borderWidth ?? 0 : 0);

    const cornerRadii = getCornerRadii(shape.props);
    const cornersUniform = areCornersUniform(cornerRadii);

    // ALWAYS render indicator, ignore isShowIndicator prop.
    // Falls back to a `<path>` when corners differ so the dashed
    // selection outline tracks the actual shape rather than rounding
    // every corner to one of the four values.
    if (!cornersUniform) {
      return (
        <path
          d={roundedRectPath(0, 0, adjustedWidth, adjustedHeight, cornerRadii)}
          stroke="#338bff"
          strokeWidth="2"
          fill="none"
          opacity="1"
          style={{
            stroke: "#338bff",
            strokeWidth: 2,
            fill: "none",
            opacity: 1,
          }}
        />
      );
    }
    return (
      <rect
        x={0}
        y={0}
        rx={rounded}
        ry={rounded}
        width={adjustedWidth}
        height={adjustedHeight}
        stroke="#338bff"
        strokeWidth="2"
        fill="none"
        opacity="1"
        style={{
          stroke: "#338bff",
          strokeWidth: 2,
          fill: "none",
          opacity: 1,
        }}
      />
    );
  }

  onResize(shape: CustomRectUtilType, info: TLResizeInfo<CustomRectUtilType>) {
    if (shape?.props?.isScale) {
      return this.shapeUtilHelper.resizeScaled(shape, info);
    } else {
      return this.shapeUtilHelper.resizeDefault(shape, info);
    }
  }
}
